import { Css, defaultTestId, IconButton, Palette, Tooltip, useModal } from "@homebound/beam";
import type { Body, Meta } from "@uppy/core";
import Uppy from "@uppy/core";
import { ReactNode, useMemo, useRef } from "react";
import { Icon } from "src/components/Icon";
import { CustomDropZone } from "src/components/uploader/UppyUploader";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";

type SvgTextUploaderProps = {
  /** Called with the dropped/selected file's raw text content. */
  onFinish: (svgText: string) => void;
  dragDropText?: string;
  dragDropWidth?: number | string;
  dragDropHeight?: number | string;
};

/**
 * A drag-and-drop SVG uploader that reads the file as text locally, rather than uploading to S3 — for fields
 * that store raw SVG markup directly (e.g. `Community.lotMap`), where there's no separate Asset to create.
 */
export function SvgTextUploader(props: SvgTextUploaderProps) {
  const { onFinish, dragDropText = "Drag & drop file", dragDropWidth = 250, dragDropHeight = 40 } = props;

  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const uppy = useMemo(() => {
    const instance = new Uppy<Meta, Body>({
      restrictions: { maxNumberOfFiles: 1, allowedFileTypes: ["image/svg+xml"] },
    });
    instance.on("files-added", async ([file]) => {
      if (!file) return;
      const svgText = await (file.data as Blob).text();
      onFinishRef.current(svgText);
      instance.removeFile(file.id);
    });
    return instance;
  }, []);

  const width = typeof dragDropWidth === "number" ? `${dragDropWidth + 2}px` : dragDropWidth;
  const height = typeof dragDropHeight === "number" ? `${dragDropHeight + 2}px` : dragDropHeight;

  return (
    <CustomDropZone
      uppy={uppy}
      allowedFileTypes={["image/svg+xml"]}
      dragDropText={dragDropText}
      width={width}
      height={height}
    />
  );
}

type SvgUploaderFieldProps = {
  /** Field label, shown above the upload/preview area. */
  label: string;
  /** Tooltip content for the info icon next to the label. */
  tooltip?: ReactNode;
  /** Tooltip content for the remove icon, and the remove-confirmation modal's message, shown once a value is present. */
  removeTooltip?: ReactNode;
  /** Title for the remove-confirmation modal. Defaults to "Remove file?". */
  removeConfirmTitle?: string;
  /** The current raw svg text, or undefined if nothing's been uploaded yet. */
  value: string | undefined;
  /** Called with the new svg text on upload, or undefined when the remove icon is clicked. */
  onChange: (svgText: string | undefined) => void;
  previewWidth?: number;
  previewHeight?: number;
  dragDropText?: string;
  dragDropWidth?: number | string;
  dragDropHeight?: number | string;
};

/**
 * A labeled field for a raw-svg-text value: an `SvgTextUploader` drop zone when empty, or an inline preview
 * (rendered via `<img>` so the browser's own scaling constrains arbitrarily-sized uploads) with a remove icon
 * once a value is present.
 */
export function SvgUploaderField(props: SvgUploaderFieldProps) {
  const {
    label,
    tooltip,
    removeTooltip,
    removeConfirmTitle = "Remove file?",
    value,
    onChange,
    previewWidth = 320,
    previewHeight = 252,
    dragDropText,
    dragDropWidth,
    dragDropHeight,
  } = props;
  const { openModal } = useModal();

  return (
    <div css={Css.sm.df.gap1.fdc.$}>
      <div css={Css.sm.df.gap1.aic.$}>
        <div>{label}</div>
        {tooltip && (
          <Tooltip title={tooltip}>
            <Icon icon="infoCircle" inc={2} color={Palette.Gray700} />
          </Tooltip>
        )}
      </div>
      {/* Rely on form autosave (or whatever `onChange` triggers) to toggle between upload and preview view */}
      {value ? (
        <div css={Css.relative.ba.bcGray100.br4.bw2.wPx(previewWidth).hPx(previewHeight).oh.df.aic.jcc.$}>
          <img src={`data:image/svg+xml;utf8,${encodeURIComponent(value)}`} alt={label} css={Css.maxw100.maxh100.$} />
          <div css={Css.absolute.top0.right0.$}>
            <IconButton
              inc={5}
              icon="trash"
              color={Palette.Red500}
              label={`Remove ${label}`}
              tooltip={removeTooltip}
              data-testid={`${defaultTestId(label)}_remove`}
              onClick={() =>
                openModal({
                  content: (
                    <ConfirmationModal
                      title={removeConfirmTitle}
                      confirmationMessage={removeTooltip ?? "Are you sure you want to remove this file?"}
                      onConfirmAction={() => onChange(undefined)}
                      label="Remove"
                      danger
                    />
                  ),
                })
              }
            />
          </div>
        </div>
      ) : (
        <SvgTextUploader
          onFinish={onChange}
          dragDropText={dragDropText}
          dragDropWidth={dragDropWidth}
          dragDropHeight={dragDropHeight}
        />
      )}
    </div>
  );
}
